import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Spinner,
} from "@heroui/react";
import { format, parseISO } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Download, MoreHorizontal, Search } from "lucide-react";
import { StatusBadge, type StatusTone } from "@/components/status-badge";
import type { Invoice } from "@/api.types";
import { useInvoices } from "@/api/use-invoices";
import { logger } from "@/services/logger";

type StatusKey =
  | "DRAFT"
  | "OPEN"
  | "OVERDUE"
  | "PAID"
  | "VOID"
  | "UNCOLLECTIBLE";

function isOverdue(invoice: Invoice): boolean {
  if (invoice.status.toUpperCase() !== "OPEN") return false;
  if (!invoice.dueDate) return false;
  return new Date(invoice.dueDate).getTime() < Date.now();
}

function getInvoiceStatus(invoice: Invoice): StatusKey {
  const raw = invoice.status.toUpperCase();
  if (raw === "OPEN" && isOverdue(invoice)) return "OVERDUE";
  if (
    raw === "DRAFT" ||
    raw === "OPEN" ||
    raw === "PAID" ||
    raw === "VOID" ||
    raw === "UNCOLLECTIBLE"
  )
    return raw;
  return "DRAFT";
}

const statusTones: Record<StatusKey, StatusTone> = {
  DRAFT: "default",
  OPEN: "primary",
  OVERDUE: "danger",
  PAID: "success",
  VOID: "default",
  UNCOLLECTIBLE: "warning",
};

type FilterKey = "all" | "open" | "overdue" | "paid";

const FILTERS: { k: FilterKey }[] = [
  { k: "all" },
  { k: "open" },
  { k: "overdue" },
  { k: "paid" },
];

function matchesFilter(invoice: Invoice, filter: FilterKey): boolean {
  if (filter === "all") return true;
  const status = getInvoiceStatus(invoice);
  if (filter === "open") return status === "OPEN" || status === "OVERDUE";
  if (filter === "overdue") return status === "OVERDUE";
  if (filter === "paid") return status === "PAID";
  return true;
}

function InvoiceReferences({
  shipmentNumbers,
  invoiceNumber,
  invoiceLabel,
}: {
  shipmentNumbers?: number[];
  invoiceNumber?: string;
  invoiceLabel?: string;
}) {
  const { t } = useTranslation("app");
  const previewLimit = 3;

  if (!shipmentNumbers?.length) {
    return (
      <span className="font-mono font-semibold text-sm text-default-900">
        {invoiceNumber ? `#${invoiceNumber}` : "—"}
      </span>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 font-mono font-semibold text-sm text-default-900">
        {shipmentNumbers.slice(0, previewLimit).map((shipmentNumber) => (
          <span key={shipmentNumber} className="break-all">
            SHP-{shipmentNumber}
          </span>
        ))}
      </div>
      {shipmentNumbers.length > previewLimit && (
        <details className="mt-1 text-xs">
          <summary className="cursor-pointer text-primary rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
            {t("invoices.moreShipments", {
              count: shipmentNumbers.length - previewLimit,
            })}
          </summary>
          <section
            aria-label={t("invoices.columns.shipments")}
            tabIndex={0}
            className="max-h-40 overflow-y-auto mt-1 flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-default-900 rounded-sm focus-visible:outline-2 focus-visible:outline-primary"
          >
            {shipmentNumbers.slice(previewLimit).map((shipmentNumber) => (
              <span key={shipmentNumber} className="break-all">
                SHP-{shipmentNumber}
              </span>
            ))}
          </section>
        </details>
      )}
      {invoiceLabel && (
        <div className="text-tiny text-default-500 mt-0.5">{invoiceLabel}</div>
      )}
    </div>
  );
}

export function CustomerInvoicesTable() {
  const { t } = useTranslation("app");
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");

  const { data, isLoading } = useInvoices();

  const invoices = useMemo(() => data ?? [], [data]);

  const filteredInvoices = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return invoices.filter((invoice) => {
      if (!matchesFilter(invoice, filter)) return false;
      if (!search) return true;
      const stripeId = invoice.stripeInvoiceId ?? "";
      const invoiceNumber = invoice.stripeInvoiceData?.number ?? "";
      const shipmentReferences =
        invoice.shipmentNumbers
          ?.flatMap((number) => [`SHP-${number}`, String(number)])
          .join(" ") ?? "";
      const orderNumbers =
        invoice?.invoiceOrders
          ?.map((io) => io.order?.orderNumber ?? "")
          .filter(Boolean)
          .join(" ") ?? "";

      return (
        stripeId.toLowerCase().includes(search) ||
        invoiceNumber.toLowerCase().includes(search) ||
        shipmentReferences.toLowerCase().includes(search) ||
        orderNumbers.toLowerCase().includes(search)
      );
    });
  }, [invoices, searchTerm, filter]);

  const getPdfUrl = (invoice: Invoice) =>
    invoice.stripeInvoiceData?.invoice_pdf ?? null;

  const getHostedUrl = (invoice: Invoice) =>
    invoice.stripeInvoiceData?.hosted_invoice_url ?? null;

  const handleDownloadPDF = (invoice: Invoice) => {
    try {
      const pdfUrl = getPdfUrl(invoice);
      if (!pdfUrl) {
        logger.error("No PDF URL available for this invoice");
        return;
      }
      window.open(pdfUrl);
    } catch (error) {
      logger.error("Error opening invoice PDF:", error);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    try {
      const hostedUrl = getHostedUrl(invoice);
      if (!hostedUrl) {
        logger.error("No hosted URL available for this invoice");
        return;
      }
      window.open(hostedUrl);
    } catch (error) {
      logger.error("Error opening invoice:", error);
    }
  };

  const columnTemplate =
    "grid grid-cols-[minmax(180px,1.3fr)_1fr_80px_120px_120px_110px_140px_44px] gap-3 px-5";

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap">
        <Input
          aria-label={t("invoices.searchLabel")}
          placeholder={t("invoices.searchPlaceholder")}
          className="sm:max-w-sm flex-1"
          value={searchTerm}
          onValueChange={setSearchTerm}
          variant="bordered"
          startContent={<Search className="h-4 w-4 text-default-400" />}
        />
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <Chip
              key={f.k}
              as="button"
              size="sm"
              variant={filter === f.k ? "solid" : "bordered"}
              color={filter === f.k ? "primary" : "default"}
              onClick={() => setFilter(f.k)}
              className="cursor-pointer"
            >
              {t(`invoices.filters.${f.k}`)}
            </Chip>
          ))}
        </div>
      </div>

      <div className="bg-content1 rounded-large shadow-small overflow-hidden">
        {/* Header row — desktop only; mobile cards carry their own labels */}
        <div
          className={`hidden md:grid ${columnTemplate} py-3 bg-default-50 border-b border-divider text-tiny font-semibold uppercase tracking-wider text-default-500`}
        >
          <div>{t("invoices.columns.shipments")}</div>
          <div>{t("invoices.columns.dateRange")}</div>
          <div>{t("invoices.columns.orders")}</div>
          <div>{t("invoices.columns.status")}</div>
          <div>{t("invoices.columns.amount")}</div>
          <div>{t("invoices.columns.due")}</div>
          <div>{t("invoices.columns.payment")}</div>
          <div />
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="py-12 grid place-items-center">
            <Spinner size="sm" color="primary" />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="py-10 text-center text-sm text-default-500">
            {t("invoices.noInvoices")}
          </div>
        ) : (
          filteredInvoices.map((invoice) => {
            const status = getInvoiceStatus(invoice);
            const overdue = status === "OVERDUE";
            const pdfUrl = getPdfUrl(invoice);
            const hostedUrl = getHostedUrl(invoice);
            const showPay = ["OPEN", "OVERDUE"].includes(status) && hostedUrl;
            const number = invoice.stripeInvoiceData?.number;
            const dateRange =
              invoice.orderDateRange?.start || invoice.orderDateRange?.end
                ? `${
                    invoice.orderDateRange?.start
                      ? format(parseISO(invoice.orderDateRange.start), "MMM d")
                      : "—"
                  } – ${
                    invoice.orderDateRange?.end
                      ? format(parseISO(invoice.orderDateRange.end), "MMM d")
                      : "—"
                  }`
                : "—";
            const amount =
              typeof invoice.totalCents === "number"
                ? `$${(invoice.totalCents / 100).toFixed(2)}`
                : "—";
            const dueLabel = invoice.dueDate
              ? format(new Date(invoice.dueDate), "MMM d")
              : null;

            return (
              <div
                key={invoice.id}
                className="border-b border-divider last:border-b-0"
              >
                {/* Mobile card */}
                <div className="md:hidden px-4 py-3.5 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <InvoiceReferences
                      shipmentNumbers={invoice.shipmentNumbers}
                      invoiceNumber={number}
                      invoiceLabel={
                        number
                          ? t("invoices.invoiceNumber", { number })
                          : undefined
                      }
                    />
                    <StatusBadge
                      tone={statusTones[status]}
                      className="shrink-0"
                    >
                      {overdue && (
                        <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                      )}
                      {t(`invoices.status.${status.toLowerCase()}`)}
                    </StatusBadge>
                  </div>
                  <div className="text-sm text-default-700">{dateRange}</div>
                  <div className="text-tiny text-default-500">
                    {t("invoices.ordersCount", {
                      count: invoice.totalOrders ?? 0,
                    })}
                    {dueLabel && (
                      <>
                        {" · "}
                        <span
                          className={
                            overdue ? "text-danger-700" : "text-default-500"
                          }
                        >
                          {t("invoices.due", { date: dueLabel })}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="font-semibold text-default-900 text-base">
                      {amount}
                    </span>
                    <div className="ml-auto flex items-center gap-1">
                      {showPay ? (
                        <Button
                          size="sm"
                          color={overdue ? "primary" : "default"}
                          variant={overdue ? "solid" : "bordered"}
                          onPress={() => handleViewInvoice(invoice)}
                        >
                          {t("invoices.payNow")}
                        </Button>
                      ) : pdfUrl ? (
                        <Button
                          size="sm"
                          variant="bordered"
                          onPress={() => handleDownloadPDF(invoice)}
                          startContent={<Download className="h-3.5 w-3.5" />}
                        >
                          PDF
                        </Button>
                      ) : null}
                      <Dropdown placement="bottom-end">
                        <DropdownTrigger>
                          <button
                            type="button"
                            aria-label={t("invoices.actions")}
                            className="w-8 h-8 grid place-items-center text-default-400 hover:text-default-700 rounded-md"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownTrigger>
                        <DropdownMenu aria-label={t("invoices.actions")}>
                          <DropdownItem
                            key="pdf"
                            onPress={() => handleDownloadPDF(invoice)}
                            isDisabled={!pdfUrl}
                          >
                            {t("invoices.downloadPdf")}
                          </DropdownItem>
                          <DropdownItem
                            key="view"
                            onPress={() => handleViewInvoice(invoice)}
                            isDisabled={!hostedUrl}
                          >
                            {t("invoices.viewInvoice")}
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </div>
                </div>

                {/* Desktop row */}
                <div
                  className={`hidden md:grid ${columnTemplate} py-3.5 items-center text-sm`}
                >
                  <div>
                    <InvoiceReferences
                      shipmentNumbers={invoice.shipmentNumbers}
                      invoiceNumber={number}
                      invoiceLabel={
                        number
                          ? t("invoices.invoiceNumber", { number })
                          : undefined
                      }
                    />
                    <div className="text-tiny text-default-500 mt-0.5">
                      {t("invoices.issued", {
                        date: format(new Date(invoice.createdAt), "MMM d"),
                      })}
                    </div>
                  </div>
                  <div className="text-default-700">{dateRange}</div>
                  <div className="text-default-700">
                    {invoice.totalOrders ?? 0}
                  </div>
                  <div>
                    <StatusBadge tone={statusTones[status]}>
                      {overdue && (
                        <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                      )}
                      {t(`invoices.status.${status.toLowerCase()}`)}
                    </StatusBadge>
                  </div>
                  <div className="font-semibold text-default-900">{amount}</div>
                  <div
                    className={overdue ? "text-danger-700" : "text-default-600"}
                  >
                    {dueLabel ?? "—"}
                  </div>
                  <div>
                    {showPay ? (
                      <Button
                        size="sm"
                        color={overdue ? "primary" : "default"}
                        variant={overdue ? "solid" : "bordered"}
                        onPress={() => handleViewInvoice(invoice)}
                      >
                        {t("invoices.payNow")}
                      </Button>
                    ) : pdfUrl ? (
                      <button
                        type="button"
                        onClick={() => handleDownloadPDF(invoice)}
                        className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                      >
                        <Download className="h-3 w-3" />
                        PDF
                      </button>
                    ) : (
                      <span className="text-default-400">—</span>
                    )}
                  </div>
                  <div>
                    <Dropdown placement="bottom-end">
                      <DropdownTrigger>
                        <button
                          type="button"
                          aria-label={t("invoices.actions")}
                          className="w-7 h-7 grid place-items-center text-default-400 hover:text-default-700 rounded-md"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label={t("invoices.actions")}>
                        <DropdownItem
                          key="pdf"
                          onPress={() => handleDownloadPDF(invoice)}
                          isDisabled={!pdfUrl}
                        >
                          {t("invoices.downloadPdf")}
                        </DropdownItem>
                        <DropdownItem
                          key="view"
                          onPress={() => handleViewInvoice(invoice)}
                          isDisabled={!hostedUrl}
                        >
                          {t("invoices.viewInvoice")}
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
